import HorizontalScrollContainer from 'components/horizontal-scroll-container'
import { MessageSendIcon } from 'components/icons'
import React, { useEffect, useRef, useState } from 'react'
import { isValidMessageText } from 'utils/validations'
import clsx from 'clsx'

interface RoomMsgTextAreaComponentProps {
  onSubmit: (newValue: string) => void
  suggestedMessages?: string[] // Optional prop for suggested messages
  placeholder?: string
}

const RoomMsgTextAreaComponent: React.FC<RoomMsgTextAreaComponentProps> = ({
  onSubmit,
  suggestedMessages = [],
  placeholder,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const [isError, setIsError] = useState(false)
  const [value, setValue] = useState<string>('')
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(null) // Track selected message

  useEffect(() => {
    // Initialize height once on mount
    const target = textareaRef.current
    if (target) {
      target.style.height = '48px' // Initial height
    }
  }, [])

  const handleInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget
    // Reset height to auto to get the correct scrollHeight
    target.style.height = 'auto'
    // Set the new height, ensuring it's between min and max heights
    const newHeight = Math.min(Math.max(target.scrollHeight, 48), 160) // 48px min, 160px max
    target.style.height = `${newHeight}px`
    setIsError(!isValidMessageText(target.value))
    setValue(target.value)
    setSelectedSuggestion(null) // Clear the selected suggestion when typing
  }

  const handleSuggestionClick = (suggestion: string) => {
    setValue(suggestion)
    setSelectedSuggestion(suggestion)
  }

  return (
    <div className='w-full'>
      {/* Suggested Messages */}
      {suggestedMessages.length > 0 && (
        <div className='mb-3'>
          <HorizontalScrollContainer>
            <div className='flex gap-2 py-1'>
              {suggestedMessages.map((suggestion, index) => (
                <button
                  key={index}
                  type='button'
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={clsx(
                    'text-sm rounded-full px-3 py-1.5 cursor-pointer transition-all duration-200',
                    'whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-custom-yellow',
                    selectedSuggestion === suggestion
                      ? 'bg-custom-yellow text-gray-900 font-medium shadow-sm'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200',
                  )}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </HorizontalScrollContainer>
        </div>
      )}

      {/* Text Area and Send Button */}
      <div className='flex items-end w-full gap-3' data-testid='messaging-testid'>
        <div className='relative flex-1'>
          <textarea
            ref={textareaRef}
            data-testid='text-area-testid'
            className={clsx(
              'w-full rounded-2xl border-0 bg-gray-50 focus:ring-2 focus:ring-custom-yellow/50',
              'px-4 py-3 pr-10 text-gray-900 shadow-sm transition-all duration-200',
              'placeholder:text-gray-400 focus:outline-none',
              isError ? 'ring-2 ring-red-500' : 'ring-1 ring-gray-300',
              'resize-none min-h-[48px] max-h-[200px] overflow-y-auto',
              'pr-10', // Add right padding for send button
            )}
            style={{ scrollbarWidth: 'thin' }}
            onKeyDown={(e) => {
              // Handle backspace/delete
              if (e.key === 'Backspace' || e.key === 'Delete') {
                // Allow default backspace/delete behavior
                return
              }

              // Send message on Ctrl+Enter or Cmd+Enter
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && isValidMessageText(value)) {
                e.preventDefault()
                onSubmit(value)
                setValue('')
                setSelectedSuggestion(null)
                if (textareaRef.current) {
                  textareaRef.current.style.height = '48px'
                }
              }
              // Allow Enter for new lines when not pressing Ctrl/Cmd
              else if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey) {
                // Allow default behavior (new line)
                return
              }
            }}
            value={value}
            onChange={handleInput}
            placeholder={placeholder || 'Type a message...'}
            rows={1}
          />
          <div className='absolute right-2 bottom-2'>
            <button
              type='button'
              data-testid='send-message-button-testid'
              onClick={() => {
                if (isValidMessageText(value)) {
                  onSubmit(value)
                  setValue('')
                  setSelectedSuggestion(null)
                  if (textareaRef.current) {
                    textareaRef.current.style.height = '48px'
                  }
                }
              }}
              disabled={!isValidMessageText(value, true)}
              className={clsx(
                'flex items-center justify-center rounded-full p-1.5 transition-all duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-custom-yellow',
                isValidMessageText(value)
                  ? 'bg-custom-yellow text-gray-900 hover:bg-yellow-400 shadow-sm'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed',
                'transform active:scale-95',
                'w-8 h-8', // Fixed size for the button
                'mb-0.5', // Slight vertical adjustment
              )}
              title={isValidMessageText(value) ? 'Send message (Ctrl+Enter)' : 'Enter a message to send'}
            >
              <MessageSendIcon disabled={!isValidMessageText(value)} />
            </button>
          </div>
        </div>

        {/* Hide scroll bar for Webkit browsers */}
        <style>
          {`
            .overflow-x-auto::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
      </div>
    </div>
  )
}

export default RoomMsgTextAreaComponent
